// `Formats` is TinyMCE's format NAMESPACE (`Formats.Formats`, `Formats.BlockFormat`),
// not the record type itself — hence the qualified names below.
import type { Editor, Formats } from 'tinymce';

/**
 * Shared, self-hosted TinyMCE configuration. Lives here (not inside a single
 * component) so every WYSIWYG surface — the universal
 * `RichTextEditorDialogComponent` and the dev `SingleFieldLlmSampleComponent` —
 * loads the editor identically (same self-hosted script, same toolbar/plugins).
 *
 * TinyMCE is loaded from `/tinymce/tinymce.min.js` (copied from
 * `node_modules/tinymce` by the `angular.json` assets glob), so there is no
 * Tiny Cloud call and no API key. `license_key: 'gpl'` selects the community
 * licence appropriate for this internal app.
 */

/** Provide via `{ provide: TINYMCE_SCRIPT_SRC, useValue: TINYMCE_SCRIPT_PATH }`. */
export const TINYMCE_SCRIPT_PATH = '/tinymce/tinymce.min.js';

/**
 * Content typography shared by the editor surface (`content_style` below) and
 * the read-only preview iframe (`rich-text-field`), so the two render
 * identically. Single source of truth — change the font/size here only.
 */
export const RICH_TEXT_CONTENT_FONT_CSS =
  "font-family: 'Times New Roman', serif; font-size: 11pt;";

/**
 * Table sizing shared by the editor surface and the read-only preview iframe —
 * the second half of the "both surfaces render identically" contract above.
 *
 * WHY THIS EXISTS. SSM-/Aspose-generated table markup reaches us with **no width
 * of any kind**: no `width=` attribute, no `width:` declaration, no
 * `<colgroup>` — every table is literally
 * `<table cellspacing="0" cellpadding="0pt" style="border-collapse:collapse;">`.
 * The government MBRS Prep Tool's viewer defaults such a table to the full
 * content width; a browser applies the CSS default `width:auto` (shrink-to-fit).
 * Same bytes, two renderings — a filing looked narrow in our editor and
 * full-width once opened at SSM. We match the Prep Tool, because that is what
 * the regulator sees.
 *
 * WHY IT IS A STYLESHEET RULE AND NOT A TINYMCE TABLE DEFAULT. Setting
 * `table_default_styles` / `table_sizing_mode` would make TinyMCE write
 * `width:100%` **into the element**, which would (a) persist into the saved HTML
 * and so change the value of a filed XBRL fact purely to fix a preview, and
 * (b) make the loaded value differ from the persisted shape, lighting the
 * sheet-selector pencil the moment a field is opened. This rule is presentation
 * only: the stored/exported HTML is untouched.
 *
 * WHY `:not([width])`. An inline `style="width:…"` already beats a stylesheet
 * rule, so author-specified widths survive on their own. The legacy `width=`
 * *attribute* is only a presentational hint and ranks BELOW author CSS, so it
 * has to be excluded explicitly or we would override it.
 */
export const RICH_TEXT_TABLE_LAYOUT_CSS = 'table:not([width]){width:100%}';

/**
 * The fonts the toolbar's `fontfamily` dropdown offers, in `Label=stack;…` form.
 *
 * CURATED, NOT TinyMCE's default 17-entry list — that one ships Symbol, Webdings,
 * Wingdings, Comic Sans MS and Impact, which have no place in a filed disclosure
 * (the symbol faces would reach the government MBRS Prep Tool as gibberish).
 *
 * **Entry #1 is load-bearing twice over.** It must stay the content default (in
 * step with {@link RICH_TEXT_CONTENT_FONT_CSS}), because TinyMCE labels the
 * dropdown by matching the computed font against these stacks — an absent content
 * default would show a raw font string on every unstyled paragraph. And it is the
 * value {@link registerFontFamilyFallback} reports for a font that is not on this
 * list at all. Reorder with care.
 *
 * Picking a font writes an inline `style="font-family:…"` into the saved HTML.
 * That is intended — it is an explicit user edit, the same reasoning recorded on
 * {@link BLOCK_FORMAT_CLEARED_STYLES}, and unlike {@link RICH_TEXT_TABLE_LAYOUT_CSS}
 * which must stay presentation-only.
 */
const RICH_TEXT_FONT_FAMILY_FORMATS =
  'Times New Roman=times new roman,times,serif;' +
  'Arial=arial,helvetica,sans-serif;' +
  'Calibri=calibri,candara,segoe,sans-serif;' +
  'Courier New=courier new,courier,monospace;' +
  'Georgia=georgia,palatino,serif;' +
  'Tahoma=tahoma,arial,helvetica,sans-serif;' +
  'Verdana=verdana,geneva,sans-serif';

/**
 * Fixed palette for the custom, localStorage-free colour buttons (see the
 * `setup` note in {@link RICH_TEXT_EDITOR_INIT}). Editing this list is the only
 * place to add/remove/reorder swatches.
 */
const RICH_TEXT_COLOR_PALETTE: ReadonlyArray<{ hex: string; name: string }> = [
  { hex: '#2E3B4E', name: 'Dark Blue' },
  { hex: '#4A90E2', name: 'Light Blue' },
  { hex: '#7FBA00', name: 'Green' },
  { hex: '#F25022', name: 'Orange' },
  { hex: '#FFB900', name: 'Yellow' },
  { hex: '#D83B01', name: 'Red' },
  { hex: '#6B4C9A', name: 'Purple' },
  { hex: '#737373', name: 'Gray' },
];

/** A single swatch cell in the colour dropdown (TinyMCE `choiceitem` shape). */
type ColorChoiceItem = {
  type: 'choiceitem';
  text: string;
  value: string;
  icon: string;
};

/** The palette as swatch cells (no "Remove color"). */
function paletteChoiceItems(): ColorChoiceItem[] {
  return RICH_TEXT_COLOR_PALETTE.map((swatch) => ({
    type: 'choiceitem' as const,
    text: swatch.name,
    value: swatch.hex,
    icon: 'checkmark',
  }));
}

/** The trailing "Remove color" swatch cell (value `'remove'`). */
const REMOVE_COLOR_ITEM: ColorChoiceItem = {
  type: 'choiceitem',
  text: 'Remove color',
  value: 'remove',
  icon: 'color-swatch-remove-color',
};

/**
 * Build the TOOLBAR split button's swatch cells (palette + a trailing "Remove
 * color"). Returned by our OWN `fetch`, which is the whole point: because we
 * never call TinyMCE's `getCurrentColors()`, the silver theme never reaches
 * `getCacheForId()` and so never seeds `localStorage['tinymce-custom-colors-*']`.
 *
 * The Format-menu swatch stays cache-free the same way, but via a different
 * seam: a `colorswatch` fancy menu item with a STATIC `initData.colors`
 * (`paletteChoiceItems()`), which makes the theme's `getColorItems()` skip its
 * `getCurrentColors()` fallback (see `registerColorMenuItems`). There the theme
 * appends "Remove color" itself, so the menu passes the palette WITHOUT it.
 */
function buildColorSwatch(): ColorChoiceItem[] {
  return [...paletteChoiceItems(), REMOVE_COLOR_ITEM];
}

/**
 * Register the custom colour controls: the toolbar split buttons AND the
 * Format-menu items (`textcolor` / `bgcolor` in both registries — TinyMCE keeps
 * button and menu-item names in separate namespaces, mirroring the built-in
 * `forecolor`/`backcolor` convention).
 *
 * These deliberately REPLACE the silver theme's built-in `forecolor` /
 * `backcolor` (kept off the toolbar/menu — see the colour note in
 * `RICH_TEXT_EDITOR_INIT`). The built-ins seed two `tinymce-custom-colors-*`
 * localStorage keys the moment their dropdown opens (an unavoidable
 * `getCurrentColors() -> getCacheForId()` write we cannot suppress by config).
 * Ours never take that path — full text/background colour with ZERO localStorage
 * keys created:
 *   - the TOOLBAR split button supplies a STATIC `fetch`;
 *   - the FORMAT-MENU item supplies a STATIC `initData.colors`, which makes the
 *     theme's `getColorItems()` skip its `getCurrentColors()` fallback.
 * Colour is applied straight through the `formatter` API (`forecolor` /
 * `hilitecolor` are core formats), wrapped in an `undoManager.transact` so each
 * change is one undo step — what the theme's `mceApplyTextcolor` does, minus the
 * colour cache.
 */
function registerColorButtons(editor: Editor): void {
  const applyColor = (format: string, hex: string): void => {
    editor.undoManager.transact(() => {
      editor.focus();
      editor.formatter.apply(format, { value: hex });
      editor.nodeChanged();
    });
  };
  const removeColor = (format: string): void => {
    editor.undoManager.transact(() => {
      editor.focus();
      editor.formatter.remove(format, { value: null }, undefined, true);
      editor.nodeChanged();
    });
  };
  const applyPicked = (format: string, value: string): void => {
    if (value === 'remove') {
      removeColor(format);
    } else {
      applyColor(format, value);
    }
  };

  // --- Toolbar split buttons ---
  const registerButton = (
    name: string,
    format: string,
    icon: string,
    tooltip: string,
    defaultHex: string,
  ): void => {
    // Mirrors the built-in "last colour" behaviour so the main button re-applies
    // the most recently picked colour — but held in a closure, not localStorage.
    let lastColor = defaultHex;
    editor.ui.registry.addSplitButton(name, {
      tooltip,
      presets: 'color',
      icon,
      columns: 4,
      fetch: (callback) => callback(buildColorSwatch()),
      onAction: () => applyColor(format, lastColor),
      onItemAction: (_api, value) => {
        if (value !== 'remove') {
          lastColor = value;
        }
        applyPicked(format, value);
      },
    });
  };

  // --- Format-menu nested items (swatch submenu; static colours = no cache) ---
  const registerMenuItem = (
    name: string,
    format: string,
    icon: string,
    text: string,
  ): void => {
    editor.ui.registry.addNestedMenuItem(name, {
      text,
      icon,
      getSubmenuItems: () => [
        {
          type: 'fancymenuitem',
          fancytype: 'colorswatch',
          initData: {
            // Static palette → `getColorItems()` uses it directly and never
            // calls `getCurrentColors()`; `allowCustomColors: false` drops the
            // "Custom color" picker (the only `addColor()` caller), so the theme
            // appends just "Remove color" and nothing is ever cached.
            allowCustomColors: false,
            colors: paletteChoiceItems(),
          },
          onAction: (data) => applyPicked(format, data.value),
        },
      ],
    });
  };

  registerButton('textcolor', 'forecolor', 'text-color', 'Text color', '#2E3B4E');
  registerButton('bgcolor', 'hilitecolor', 'highlight-bg-color', 'Background color', '#FFB900');
  registerMenuItem('textcolor', 'forecolor', 'text-color', 'Text color');
  registerMenuItem('bgcolor', 'hilitecolor', 'highlight-bg-color', 'Background color');
}

/**
 * Inline style declarations cleared from the element when a block format is
 * applied, keyed by the block tag the user picked in the toolbar's "Paragraph"
 * dropdown.
 *
 * WHY THIS EXISTS. TinyMCE defines every block format as
 * `{ block: 'h1', remove: 'all' }` with no `preserve_attributes`, and applies it
 * with `dom.rename(node, 'h1')` — which copies **every attribute, including the
 * inline `style`, onto the new element**. SSM-/TX27-generated disclosure markup
 * (what these fields are hydrated with) puts `font-size:11pt`,
 * `margin-top:0pt` and `margin-bottom:0pt` on EVERY `<p>`. Those copied
 * declarations outrank the browser's heading defaults, so picking "Heading 1"
 * produced a real `<h1>` that rendered at the paragraph's exact size with no
 * heading spacing — the dropdown looked completely dead on any filing loaded
 * from an extraction, while working fine on text typed from scratch.
 *
 * WHY THIS ONE REACHES THE MARKUP, UNLIKE {@link RICH_TEXT_TABLE_LAYOUT_CSS}.
 * The table rule is deliberately presentation-only because the source markup
 * carries NO width, so a stylesheet default cannot contradict anything the
 * author wrote. Here the source carries a **conflicting** `font-size`, so a
 * display-only override would show the preparer a heading that the government
 * MBRS Prep Tool would still render as body text. We clear the conflict at the
 * source instead, so the saved fact renders as a heading everywhere — which is
 * the same "match what the regulator sees" goal, reached the other way round.
 * Applying a heading is an explicit user edit, so changing the stored HTML here
 * is intended, not a side effect of opening a sheet.
 *
 * WHAT IS DELIBERATELY KEPT: `text-align`, `text-indent` and `margin-left` (SSM
 * uses the left margin for the 12.1 / 12.2 outline indent, which must survive),
 * `font-family` (so a font picked from the toolbar survives a later heading
 * change), and `lang`. Only the declarations that fight the chosen block are
 * cleared.
 *
 * Headings are the whole record: `block_formats` offers nothing else that
 * carries its own typography (see the Preformatted note there).
 *
 * NOT THE WHOLE STORY — see {@link DESCENDANT_CLEARED_STYLES}. Clearing the block
 * element alone fixes a plain `<p style="font-size:11pt">`, but not a
 * `<p><span style="font-size:11pt">` (the shape a paste produces), where the size
 * lives one level down and still outranks the heading.
 */
const BLOCK_FORMAT_CLEARED_STYLES: Readonly<Record<string, readonly string[]>> =
  (() => {
    // Size and weight come from the heading itself; the 0pt block margins would
    // otherwise leave it visually welded to the surrounding paragraphs.
    const heading = ['font-size', 'font-weight', 'margin-top', 'margin-bottom'];
    return {
      h1: heading,
      h2: heading,
      h3: heading,
      h4: heading,
      h5: heading,
      h6: heading,
    };
  })();

/**
 * Inline declarations cleared from every **descendant** of the element a block
 * format was just applied to — the other half of {@link BLOCK_FORMAT_CLEARED_STYLES}.
 *
 * WHY THIS EXISTS. Clearing the block element alone leaves the heading suppressed
 * whenever the size sits one level down, and two everyday actions produce exactly
 * that shape:
 *
 *  - **Pasting.** TinyMCE carries formatting across as an inline wrapper, so a
 *    pasted line is `<p><span style="font-size:11pt">…</span></p>`. Renaming the
 *    `<p>` to `<h1>` leaves the span untouched, and an inline `font-size` on the
 *    child beats the heading's own. Measured: the block's margins cleared (so the
 *    line gap grew) while the text stayed at 14.67px — "the spacing changes but
 *    the size doesn't".
 *  - **Lists and table cells.** `li` / `td` / `th` are NOT text-block elements in
 *    TinyMCE's schema (`textBlockElementsMap` is headings + `p div address pre form
 *    blockquote …`), so the formatter cannot rename them and *wraps* instead —
 *    `<li><h1><span style="font-size:11pt">…</span></h1></li>`. Same suppression,
 *    reached a different way.
 *
 * WHY IT IS NARROWER THAN THE BLOCK'S OWN LIST. Only the two declarations a heading
 * actually defines are cleared. Margins are excluded (meaningless on an inline span,
 * and on a nested block they are the author's own spacing); `font-family` is
 * excluded for the same reason the block-level list excludes it — a font picked from
 * the toolbar must survive a heading change; `color` / `background-color` are
 * excluded because they are deliberate choices from the colour buttons, not
 * inherited noise.
 *
 * ACCEPTED TRADE-OFF: a deliberately mixed size *within* one line is flattened when
 * that line becomes a heading. Inside a heading that is far likelier to be inherited
 * noise than intent, and applying a heading is an explicit user edit — the same
 * reasoning recorded on {@link BLOCK_FORMAT_CLEARED_STYLES}.
 */
const DESCENDANT_CLEARED_STYLES: readonly string[] = ['font-size', 'font-weight'];

/**
 * Re-register the block formats listed in {@link BLOCK_FORMAT_CLEARED_STYLES} so
 * applying one also clears the inline declarations that would suppress it.
 *
 * Two implementation details are load-bearing:
 *
 *  1. **`editor.dom.setStyle`, never `elm.style`.** TinyMCE keeps a
 *     `data-mce-style` shadow attribute and restores `style` from it while
 *     serialising, so a direct `elm.style.removeProperty()` fixes what is on
 *     screen but leaves the SAVED value untouched — `getContent()` still emits
 *     the original `font-size`. Going through DOMUtils updates both.
 *  2. **Registration is deferred to `init`.** `setup` runs before the formatter
 *     exists. Late registration is safe because the toolbar's block dropdown
 *     resolves formats by NAME at apply time (and `formatter.match` likewise),
 *     so the button, its label and its checkmark all pick these up.
 *
 * `onformat` runs inside `setElementFormat`, immediately after the `dom.rename`
 * that copied the attributes across — so we are clearing them on the new
 * element, before the user ever sees it.
 */
function registerBlockFormats(editor: Editor): void {
  const formats: Formats.Formats = {};
  for (const [tag, clearedStyles] of Object.entries(
    BLOCK_FORMAT_CLEARED_STYLES,
  )) {
    const format: Formats.BlockFormat = {
      block: tag,
      remove: 'all',
      onformat: (elm) => {
        for (const style of clearedStyles) {
          editor.dom.setStyle(elm, style, '');
        }
      },
    };
    formats[tag] = format;
  }
  editor.on('init', () => editor.formatter.register(formats));
  registerHeadingCleanup(editor);
}

/**
 * Take a heading out of the list it was applied inside, matching the government
 * MBRS Prep Tool.
 *
 * WHY. TinyMCE's own behaviour is to keep the bullet and nest the heading in it
 * (`<li><h1>…</h1></li>`), so the line reads as a numbered item that happens to be
 * large. The Prep Tool — the renderer the regulator actually uses — instead removes
 * the item from the list and leaves a standalone heading. Verified against the tool
 * by the PO, so the Prep Tool wins.
 *
 * NUMBERING. The list splits, and the items below renumber **as if the extracted
 * one had never been there** — converting "2. Bravo" of Alpha/Bravo/Charlie/Delta
 * gives `1. Alpha`, the Bravo heading, then `2. Charlie`, `3. Delta`. TinyMCE's
 * `RemoveList` splits correctly but leaves the lower list restarting at 1, so the
 * `start` attribute is set here: the upper list's own `start` plus the number of
 * items left in it. `<ul>` has no numbering and needs none.
 *
 * Table cells are deliberately untouched — a heading inside a `<td>` has nowhere to
 * escape to, and `RemoveList` only acts on lists.
 */
function liftHeadingOutOfList(
  editor: Editor,
  heading: Element,
  tag: string,
): void {
  if (!editor.dom.getParent(heading, 'li')) {
    return;
  }
  editor.execCommand('RemoveList');

  // `RemoveList` rebuilds the nodes around the split, so the element we were
  // handed is detached by now and its siblings would read as null — re-resolve the
  // heading from where the selection ended up.
  const lifted = editor.dom.getParent(editor.selection.getNode(), tag);
  if (!lifted) {
    return;
  }
  const upper = lifted.previousElementSibling;
  const lower = lifted.nextElementSibling;
  if (upper?.nodeName !== 'OL' || lower?.nodeName !== 'OL') {
    return;
  }
  const upperStart = parseInt(editor.dom.getAttrib(upper, 'start') || '1', 10);
  const lowerStart = upperStart + upper.children.length;
  if (lowerStart > 1) {
    editor.dom.setAttrib(lower, 'start', String(lowerStart));
  }
}

/**
 * Everything that has to happen *after* a heading has been applied, in one
 * `FormatApply` listener.
 *
 * WHY `FormatApply` AND NOT `onformat`. The formatter reaches a block format two
 * ways, and only one of them has the content in place when `onformat` runs:
 *
 *  - **Rename** (`<p>` → `<h1>`): `setElementFormat` fires on the renamed element,
 *    children and all.
 *  - **Wrap** (inside `<li>` / `<td>`, which are not text-block elements in
 *    TinyMCE's schema so cannot be renamed): `createWrapElement` calls
 *    `dom.create(wrapName)` and formats that element **while it is still empty**,
 *    moving the content in afterwards.
 *
 * A descendant sweep in `onformat` therefore fixed the paste shape and left the list
 * and table-cell shapes exactly as broken — measured. `FormatApply` fires once the
 * apply has completed and sees the finished tree in both cases, and
 * `getSelectedBlocks()` covers a multi-block selection rather than only the block
 * the caret sits in.
 */
function registerHeadingCleanup(editor: Editor): void {
  editor.on('FormatApply', (event: { format?: string }) => {
    const tag = (event.format ?? '').toLowerCase();
    if (!(tag in BLOCK_FORMAT_CLEARED_STYLES)) {
      return;
    }
    for (const block of editor.selection.getSelectedBlocks()) {
      // The applied heading is the block itself when renamed, or an ancestor when
      // the formatter wrapped inside a list item / table cell. Bail out when it is
      // neither, rather than falling back to `block`: clearing styles off a block
      // that did NOT become a heading would be a content change with no visible
      // formatting to justify it — the sheet would go dirty for nothing.
      const heading = editor.dom.getParent(block, tag);
      if (!heading) {
        continue;
      }
      // Clear BEFORE lifting: the sweep reads the tree the format just produced,
      // and lifting moves that subtree around.
      for (const descendant of editor.dom.select('[style]', heading)) {
        for (const style of DESCENDANT_CLEARED_STYLES) {
          editor.dom.setStyle(descendant, style, '');
        }
      }
      liftHeadingOutOfList(editor, heading, tag);
    }
  });
}


/** Lowercase, unquote, and squeeze the spaces out of a CSS font-family value. */
function normalizeFontValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/\s*,\s*/g, ',')
    .trim();
}

/** The first face of a font stack, normalised (`"Arial", sans-serif` → `arial`). */
function firstFontOf(value: string): string {
  return normalizeFontValue(value).split(',')[0] ?? '';
}

/** The stacks from {@link RICH_TEXT_FONT_FAMILY_FORMATS}, in listed order. */
function fontFamilyStacks(): string[] {
  return RICH_TEXT_FONT_FAMILY_FORMATS.split(';').map(
    (entry) => entry.split('=')[1] ?? '',
  );
}

/**
 * Would the `fontfamily` dropdown recognise this value as one of its own entries?
 *
 * Deliberately mirrors the silver theme's `getMatchingValue()`: an entry matches
 * on **whole-stack** equality OR on sharing the stack's **first face**. That second
 * arm is why bare `Arial` from an extraction matches our
 * `arial,helvetica,sans-serif` — and mirroring it here is what stops
 * {@link registerFontFamilyFallback} from firing on a font the dropdown would have
 * matched perfectly well.
 */
function isSelectableFont(value: string): boolean {
  const font = normalizeFontValue(value);
  const first = firstFontOf(value);
  return fontFamilyStacks().some((stack) => {
    const candidate = normalizeFontValue(stack);
    return candidate === font || firstFontOf(candidate) === first;
  });
}

/**
 * Report the content default for a font the dropdown cannot offer, so the picker
 * never names a font the preparer is unable to select.
 *
 * THE BUG THIS FIXES. Extracted SSM/TX27 markup carries fonts that are not on our
 * curated list — measured across `src/assets/dev/sample-conversions/*.json`, real
 * content uses `'Arial'` (5,199×), `'Aptos Narrow'` (3,009×) and `'Verdana'` (135×),
 * and `Aptos Narrow` (the Microsoft 365 default) is on none of the machines that
 * lack Office. The silver theme's `updateSelectMenuText` ends
 * `matchOpt.fold(constant(font), (item) => item.title)` — with no match it prints
 * the RAW font string. So the button read "Aptos Narrow" while the paragraph
 * rendered in the browser's fallback face, and no dropdown entry was ticked.
 *
 * WHY THIS SEAM. The theme resolves the label from
 * `editor.queryCommandValue('FontName')` and exposes no hook on the label itself —
 * its `FontFamilyTextUpdate` event only drives the tooltip; the button text is
 * emitted straight to the component. The obvious alternative,
 * `editor.addQueryValueHandler('FontName', …)`, *overwrites* the entry in
 * `EditorCommands`' private `commands.value` map, so it cannot delegate to the
 * handler it replaces (and that map is not public API). Overriding the public
 * method keeps TinyMCE's own implementation callable.
 *
 * DISPLAY ONLY. The stored HTML is never rewritten: applying a font goes through
 * `execCommand`, which is untouched, so picking still works and no filed XBRL fact
 * changes as a side effect of opening a sheet. The accepted cost is that a preparer
 * who DOES have the font sees it rendered while the picker says Times New Roman —
 * labelling by real availability would need per-machine font probing, which is a
 * browser-dependent answer we are not putting inside a filing tool.
 *
 * Only `FontName` is intercepted, and its only readers are the toolbar button and
 * the `Format → Fonts` menu item (they share one spec), so the blast radius is
 * exactly the two font pickers.
 */
function registerFontFamilyFallback(editor: Editor): void {
  const fallback = fontFamilyStacks()[0] ?? '';
  const queryOriginal = editor.queryCommandValue.bind(editor);

  editor.queryCommandValue = (command: string): string => {
    const value = queryOriginal(command);
    if (command.toLowerCase() !== 'fontname') {
      return value;
    }
    // Empty means TinyMCE could not resolve a font at all — its own query ends
    // `mapRange(…).getOr('')`. Pass that through: an unresolved query should stay
    // unresolved (blank button), not be relabelled as a font we did not find.
    if (value === '' || isSelectableFont(value)) {
      return value;
    }
    return fallback;
  };
}

/**
 * Everything this config registers on the editor instance. Kept as one named
 * function (rather than inlining a lambda on `setup`) so each concern stays a
 * separately documented, separately testable registrar.
 *
 * NOTE: `TinyMceWarmupService` spreads this config but REPLACES `setup` for its
 * throwaway warm-up editor — that instance exists only to pull the theme/skin/
 * plugin assets into the browser cache and is torn down on `init`, so it does
 * not need (and must not be given) any of this. Do not "fix" that omission.
 */
function registerEditorExtensions(editor: Editor): void {
  registerColorButtons(editor);
  registerBlockFormats(editor);
  // Registered immediately, unlike `registerBlockFormats` — that one defers to
  // `init` because the formatter does not exist during `setup`; this only wraps a
  // method that is already on the editor.
  registerFontFamilyFallback(editor);
}

/** Self-hosted init shared by every rich-text surface. */
export const RICH_TEXT_EDITOR_INIT = {
  license_key: 'gpl',
  base_url: '/tinymce',
  suffix: '.min',
  branding: false,
  promotion: false,
  statusbar: false,
  height: 600,
  min_height: 400,
  // 'view' is intentionally omitted from the menubar — there is no Source-code
  // / Visual-aids toggle for the user. Visual aids stay ON (see `visual` below)
  // so borderless tables remain visible while editing.
  menubar: 'file edit insert format table',
  // Visual aids (dashed guides around borderless tables/cells) on by default.
  // This is TinyMCE's default too, but set explicitly since the View menu that
  // would otherwise toggle it has been removed.
  visual: true,
  // --- Text / background colour: enabled, WITHOUT any localStorage keys ---
  // Requirement (AS-897, R18): the `tinymce-custom-colors-forecolor` /
  // `-hilitecolor` localStorage keys must NEVER be created. The silver theme has
  // two seed paths into `getCacheForId()` (`localStorage.setItem('tinymce-custom-
  // colors-<id>', '[]')`); both are closed here so colour can be enabled safely:
  //
  //  1. INIT-TIME — the theme UNCONDITIONALLY registers the built-in
  //     `forecolor`/`backcolor` split buttons and evaluates their `tooltip`
  //     eagerly; with a non-empty "last colour" (default `#000000`) that falls
  //     through to `getCurrentColors() -> getCacheForId()`. Empty
  //     `color_default_*` makes `getToolTipText()` take its `isEmpty` early-
  //     return, so registration seeds nothing.
  //  2. DROPDOWN-OPEN — the built-ins' `fetch` calls `getCurrentColors()`
  //     unconditionally. We simply never expose them: they are absent from the
  //     toolbar AND dropped from the Format menu (`removed_menuitems`), so their
  //     dropdown never opens. The actual colour UI is our OWN `textcolor` /
  //     `bgcolor` split buttons registered in `setup` (see `registerColorButtons`),
  //     whose static `fetch` never touches the colour cache.
  //
  // Net effect: full text/background colour, and NO `tinymce-custom-colors-*`
  // keys are ever written.
  color_default_foreground: '',
  color_default_background: '',
  removed_menuitems: 'forecolor backcolor',
  // 4-column swatch for the Format-menu colour submenus (the toolbar split
  // buttons set their own `columns: 4`; this covers the menu path, which derives
  // its column count from `color_cols`). Keeps both swatch surfaces consistent.
  color_cols: 4,
  setup: registerEditorExtensions,
  // Re-define the Format menu so our custom `textcolor` / `bgcolor` items sit
  // directly above "Clear formatting" (`removeformat`). Mirrors TinyMCE's default
  // Format-menu order with the built-in `forecolor backcolor` swapped for ours.
  menu: {
    format: {
      title: 'Format',
      items:
        'bold italic underline strikethrough superscript subscript codeformat | ' +
        'styles blocks fontfamily fontsize align lineheight | ' +
        'textcolor bgcolor | removeformat',
    },
  },
  plugins: 'lists advlist table autolink',
  // TinyMCE's default block list ends with `Preformatted=pre`, and there is no
  // switch to drop a single entry — so the list is restated here without it.
  // A `<pre>` is a monospace, whitespace-preserving code block: never something a
  // preparer means to put in an SSM disclosure note, and picking it by accident
  // would bake the tag into a filed XBRL fact. This one option feeds BOTH the
  // toolbar's `blocks` dropdown and the Format menu's `blocks` item.
  block_formats:
    'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;' +
    'Heading 4=h4;Heading 5=h5;Heading 6=h6',
  font_family_formats: RICH_TEXT_FONT_FAMILY_FORMATS,
  // `fontfamily` sits on the toolbar (as well as in the Format menu, which keeps
  // its own copy) because it doubles as a READOUT: it names the font at the
  // cursor, which is how a preparer tells inherited SSM markup from their own
  // edits without opening a menu.
  toolbar:
    'undo redo | fontfamily blocks | bold italic underline | textcolor bgcolor | ' +
    'alignleft aligncenter alignright alignjustify | outdent indent | table',
  // The editing surface font + table sizing. The read-only preview iframe
  // injects the same two constants, so the two surfaces render identically.
  // Note there is deliberately NO `table_default_styles` / `table_sizing_mode`
  // here — see {@link RICH_TEXT_TABLE_LAYOUT_CSS} for why sizing must stay a
  // stylesheet rule and never be baked into the emitted markup.
  content_style: `body { ${RICH_TEXT_CONTENT_FONT_CSS} } ${RICH_TEXT_TABLE_LAYOUT_CSS}`,
};
